import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import App from './App'
import { ThemeProvider, useTheme } from './context/ThemeContext'
import { msalConfig } from './lib/auth'
import { isDemoMode } from './lib/demo/flag'
import './index.css'

// Demo build runs without MSAL — no Microsoft login, no token cache,
// no auth token-request side effects. The api client's demo adapter
// resolves every request from local mock data, and App skips the
// AuthenticatedTemplate gate entirely.
const msalInstance = isDemoMode ? null : new PublicClientApplication(msalConfig)
if (msalInstance) {
  ;(window as any).msalInstance = msalInstance
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

/**
 * Theme-aware toaster. react-hot-toast doesn't read CSS variables for its
 * default styles, so we have to explicitly thread theme through inline
 * styles. Lifted into a ThemeProvider child component (useTheme would
 * throw above the provider).
 */
const ThemedToaster: React.FC = () => {
  const { theme } = useTheme()
  const isLight = theme === 'light'
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: isLight ? '#ffffff' : '#1f2937',
          color: isLight ? '#0f172a' : '#f9fafb',
          border: isLight ? '1px solid #e2e8f0' : '1px solid #374151',
        },
      }}
    />
  )
}

const InnerTree = (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <App />
      <ThemedToaster />
    </QueryClientProvider>
  </BrowserRouter>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      {msalInstance ? (
        <MsalProvider instance={msalInstance}>{InnerTree}</MsalProvider>
      ) : (
        InnerTree
      )}
    </ThemeProvider>
  </React.StrictMode>,
)
